import { type FC, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { trackNavInteraction } from '../../helpers/tracking';
import QuestionMarkCircle from 'dibs-icons/exports/QuestionMarkCircle';
import { NavSupportItems } from './NavSupportItems';
import { interactionTypeConstants } from 'dibs-tracking';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

import { type NavHelp_seller$key } from './__generated__/NavHelp_seller.graphql';

type Props = {
    seller?: NavHelp_seller$key | null;
};

const sellerFragment = graphql`
    fragment NavHelp_seller on Seller {
        sellerRep {
            email
        }
    }
`;

export const NavHelp: FC<Props> = ({ seller: sellerRef }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const [isMenuVisible, setIsMenuVisible] = useState(false);

    const accountManagerEmail = seller?.sellerRep?.email;
    const supportEventName = 'Support';

    useEffect(() => {
        if (isMenuVisible) {
            trackNavInteraction({
                action: `${supportEventName} dropdown`,
                label: supportEventName,
                interactionType: interactionTypeConstants.INTERACTION_TYPE_DROP_DOWN,
                isImpression: true,
            });
        }
    }, [isMenuVisible]);

    return (
        <div
            onClick={() => {
                trackNavInteraction({
                    action: supportEventName,
                    label: supportEventName,
                    interactionType: interactionTypeConstants.INTERACTION_TYPE_GLOBAL_NAV,
                });
                setIsMenuVisible(prev => !prev);
            }}
            className={classNames(
                dibsCss.relative,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.itemsCenter,
                dibsCss.sassyFontBodySmallHeavy,
                dibsCss.cursorPointer
            )}
            data-tn="navbar-help"
        >
            <QuestionMarkCircle variation="20" />
            <FormattedMessage id="dal.header.help" defaultMessage="Help" />
            <Tooltip
                isVisible={isMenuVisible}
                direction="bottom"
                align="left"
                paddingSize="none"
                closeOnOutsideClick
                onClose={() => setIsMenuVisible(false)}
            >
                <div
                    className={classNames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.gapXsmall,
                        dibsCss.pSmall
                    )}
                >
                    <NavSupportItems />
                    {accountManagerEmail && (
                        <div
                            className={classNames(
                                dibsCss.flex,
                                dibsCss.flexCol,
                                dibsCss.ptXsmall,
                                dibsCss.border0,
                                dibsCss.borderT,
                                dibsCss.borderSolid,
                                dibsCss.borderDolphin
                            )}
                        >
                            <FormattedMessage
                                id="dal.navItem.partnerManager.email"
                                defaultMessage="<wrapper>Partner Manager:</wrapper><wrapper>{email}</wrapper>"
                                values={{
                                    email: accountManagerEmail,
                                    wrapper: content => {
                                        return <span>{content}</span>;
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};
